import React, { useEffect, useRef, useState } from "react";
import { trackButtonClick } from "../analytics/ga";
import { CHROME_EXTENSION_URL } from "../constants/links";
import { SUPPORTED_LANGUAGES } from "../i18n";
import { useAuth } from "../auth/AuthContext";
import { trackEvent } from "../analytics/ga";

export const Navigation = ({ ui, language, onLanguageChange }) => {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const languageMenuRef = useRef(null);
  const navLinksRef = useRef(null);

  useEffect(() => {
    document.body.classList.toggle("mobile-menu-open", menuOpen);
    return () => document.body.classList.remove("mobile-menu-open");
  }, [menuOpen]);

  useEffect(() => {
    const closeMenu = () => {
      setMenuOpen(false);
      setOpenSubmenu(null);
    };
    window.addEventListener("resize", closeMenu);
    return () => window.removeEventListener("resize", closeMenu);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target)
      ) {
        setLanguageMenuOpen(false);
      }

      if (
        navLinksRef.current &&
        !navLinksRef.current.contains(event.target)
      ) {
        setOpenSubmenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeMenu = () => {
    setMenuOpen(false);
    setOpenSubmenu(null);
  };

  const isDesktopNav = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(min-width: 768px)").matches;

  const toggleSubmenu = (key) => {
    if (isDesktopNav()) return;
    setOpenSubmenu((current) => (current === key ? null : key));
  };

  const openSubmenuOnHover = (key) => {
    if (!isDesktopNav()) return;
    setOpenSubmenu(key);
  };

  const closeSubmenuOnLeave = () => {
    if (!isDesktopNav()) return;
    setOpenSubmenu(null);
  };

  const currentLanguage =
    SUPPORTED_LANGUAGES.find(({ code }) => code === language) ??
    SUPPORTED_LANGUAGES[0];

  const handleLanguageSelect = (code) => {
    onLanguageChange(code);
    setLanguageMenuOpen(false);
  };

  const solutionsItems = [
    { href: "#integrations", label: ui?.integrations },
    { href: "#business", label: ui?.business },
  ];

  const resourcesItems = [
    { href: "#privacy", label: ui?.privacy },
    { href: "#faq", label: ui?.faq },
    { href: "#contact", label: ui?.contact },
  ];

  const renderSubmenu = (key, label, items) => {
    const isOpen = openSubmenu === key;

    return (
      <li
        className={`nav-dropdown ${isOpen ? "open" : ""}`}
        onMouseEnter={() => openSubmenuOnHover(key)}
        onMouseLeave={closeSubmenuOnLeave}
      >
        <button
          type="button"
          className="nav-dropdown-toggle"
          aria-expanded={isOpen}
          aria-haspopup="true"
          onClick={() => toggleSubmenu(key)}
        >
          <span>{label}</span>
          <span className="nav-dropdown-chevron" aria-hidden="true">
            ▾
          </span>
        </button>
        <ul className="nav-dropdown-menu" role="menu">
          {items.map(({ href, label: itemLabel }) => (
            <li key={href} role="none">
              <a
                href={href}
                className="page-scroll"
                role="menuitem"
                onClick={closeMenu}
              >
                {itemLabel}
              </a>
            </li>
          ))}
        </ul>
      </li>
    );
  };

  return (
    <nav id="menu" className="navbar navbar-default navbar-fixed-top">
      <div className="container">
        <div className="navbar-header">
          <a className="navbar-brand page-scroll" href="#page-top" onClick={closeMenu}>
            FastLex
          </a>
          <button
            type="button"
            className={`navbar-toggle ${menuOpen ? "" : "collapsed"}`}
            aria-expanded={menuOpen}
            aria-controls="bs-example-navbar-collapse-1"
            onClick={() => {
              setMenuOpen((open) => !open);
              setOpenSubmenu(null);
            }}
          >
            <span className="sr-only">{ui?.toggleNavigation}</span>
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
          </button>
        </div>

        <div
          className={`collapse navbar-collapse ${menuOpen ? "in" : ""}`}
          id="bs-example-navbar-collapse-1"
        >
          <ul className="nav navbar-nav navbar-right navbar-links" ref={navLinksRef}>
            <li>
              <a href="/playground" onClick={closeMenu}>
                {ui?.playground}
              </a>
            </li>
            <li>
              <a
                href="#chrome-extension"
                className="page-scroll"
                onClick={closeMenu}
              >
                {ui?.chromeExtension}
              </a>
            </li>
            <li>
              <a href="#pricing" className="page-scroll" onClick={closeMenu}>
                {ui?.pricing}
              </a>
            </li>
            {renderSubmenu("solutions", ui?.solutions, solutionsItems)}
            {renderSubmenu("resources", ui?.resources, resourcesItems)}
          </ul>
          <div className="navbar-actions">
            {!user && (
            <div className="language-dropdown" ref={languageMenuRef}>
              <button
                type="button"
                className="language-dropdown-toggle"
                onClick={() => setLanguageMenuOpen((open) => !open)}
                aria-expanded={languageMenuOpen}
                aria-haspopup="listbox"
                aria-label={ui?.selectLanguage}
              >
                <span className="language-dropdown-label">
                  {currentLanguage.label}
                </span>
                <span className="language-dropdown-chevron" aria-hidden="true">
                  ▾
                </span>
              </button>
              {languageMenuOpen && (
                <ul className="language-dropdown-menu" role="listbox">
                  {SUPPORTED_LANGUAGES.map(({ code, name }) => (
                    <li key={code} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={language === code}
                        className={`language-dropdown-item ${language === code ? "active" : ""
                          }`}
                        onClick={() => handleLanguageSelect(code)}
                      >
                        {name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            )}
            <a
              href={user ? "/app/snippets" : "/login"}
              className="nav-signin-link"
              onClick={() => {
                trackButtonClick(user ? "nav_open_app" : "nav_sign_in");
                closeMenu();
              }}
            >
              {user ? ui?.openApp || "Dashboard" : ui?.signIn || "Sign in"}
            </a>
            <a
              href={CHROME_EXTENSION_URL}
              className="nav-download-btn"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent({ category: "Navigation", action: "Download Extension" });
                closeMenu();
              }}
            >
              {ui?.requestEarlyAccess}
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};
